/**
 * تاجر برو — Main Application
 * - React.lazy + Suspense for route-level code splitting
 * - ThemeProvider wraps entire app for dark/light mode persistence
 * - ErrorBoundary catches unhandled JS errors globally
 * - Skip-to-content link for keyboard / screen-reader accessibility
 * - AI Chat Widget rendered only when user is authenticated
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { SuccessCelebrationProvider } from './components/ui/SuccessCelebration';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { BranchProvider } from './context/BranchContext';
import ErrorBoundary from './components/ErrorBoundary';
import PageErrorBoundary from './components/PageErrorBoundary';
import AIChatWidget from './components/AIChatWidget';
import FloatingAIAssistant from './components/FloatingAIAssistant';
import { Layout } from './components/Layout';
import DocumentTitle from './components/DocumentTitle';
import { queryClient } from './lib/queryClient';
import { DensityProvider } from './design-system';
import './index.css';

// ─── Skeleton fallback shown while lazy chunks load ───────────────────────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" aria-busy="true" aria-label="جار التحميل">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      <p className="text-sm text-muted-foreground animate-pulse">جار التحميل…</p>
    </div>
  </div>
);

// ─── Lazy-loaded pages (code splitting) ──────────────────────────────────
// Public
const LoginPage    = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage ?? m.default })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage ?? m.default })));
const StorefrontPage = lazy(() => import('./pages/StorefrontPage').then(m => ({ default: m.StorefrontPage ?? m.default })));

// Dashboard
const UnifiedDashboard   = lazy(() => import('./pages/UnifiedDashboard'));

// Core ERP
const CustomersPage      = lazy(() => import('./pages/CustomersPage').then(m => ({ default: m.CustomersPage ?? m.default })));
const Customer360Page    = lazy(() => import('./pages/Customer360Page'));
const SuppliersPage      = lazy(() => import('./pages/SuppliersPage').then(m => ({ default: m.SuppliersPage ?? m.default })));
const ProductsPage       = lazy(() => import('./pages/ProductsPage').then(m => ({ default: m.ProductsPage ?? m.default })));
const InventoryOverviewPage = lazy(() => import('./pages/InventoryOverviewPage'));
const EmployeesPage      = lazy(() => import('./pages/EmployeesPage').then(m => ({ default: m.EmployeesPage ?? m.default })));
const InvoicesPage       = lazy(() => import('./pages/InvoicesPage').then(m => ({ default: m.InvoicesPage ?? m.default })));
const RecurringInvoicesPage = lazy(() => import('./pages/RecurringInvoicesPage'));
const PaymentsOverviewPage = lazy(() => import('./pages/PaymentsOverviewPage'));
const CreateInvoicePage  = lazy(() => import('./pages/CreateInvoicePage').then(m => ({ default: m.CreateInvoicePage ?? m.default })));
const InvoiceDetailsPage = lazy(() => import('./pages/InvoiceDetailsPage').then(m => ({ default: m.InvoiceDetailsPage ?? m.default })));
const SalesPage          = lazy(() => import('./pages/SalesPage').then(m => ({ default: m.SalesPage ?? m.default })));
const PurchasesPage      = lazy(() => import('./pages/PurchasesPage').then(m => ({ default: m.PurchasesPage ?? m.default })));
const ExpensesPage       = lazy(() => import('./pages/ExpensesPage').then(m => ({ default: m.ExpensesPage ?? m.default })));
const ReportsPage        = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage ?? m.default })));
const QuotationsPage     = lazy(() => import('./pages/QuotationsPage').then(m => ({ default: m.QuotationsPage ?? m.default })));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage').then(m => ({ default: m.PurchaseOrdersPage ?? m.default })));
const CRMPage            = lazy(() => import('./pages/CRMPage').then(m => ({ default: m.CRMPage ?? m.default })));
const ManufacturingPage  = lazy(() => import('./pages/ManufacturingPage').then(m => ({ default: m.ManufacturingPage ?? m.default })));
const ManufacturingRoadmapPage = lazy(() => import('./pages/ManufacturingRoadmapPage').then(m => ({ default: m.ManufacturingRoadmapPage ?? m.default })));
const WarehousesPage     = lazy(() => import('./pages/WarehousesPage').then(m => ({ default: m.WarehousesPage ?? m.default })));
const BranchesPage       = lazy(() => import('./pages/BranchesPage'));
const StockMovesPage     = lazy(() => import('./pages/StockMovesPage').then(m => ({ default: m.StockMovesPage ?? m.default })));
const AccountingPage     = lazy(() => import('./pages/AccountingPage').then(m => ({ default: m.AccountingPage ?? m.default })));
const AccountingCloseDashboardPage = lazy(() => import('./pages/AccountingCloseDashboardPage'));
const JournalEntriesPage = lazy(() => import('./pages/JournalEntriesPage'));
const FinancialReportsPage = lazy(() => import('./pages/FinancialReportsPage'));
const BanksPage          = lazy(() => import('./pages/BanksPage'));
const BankReconciliationPage = lazy(() => import('./pages/BankReconciliationPage'));
const PromotionsPage     = lazy(() => import('./pages/PromotionsPage'));
const LoyaltyPage        = lazy(() => import('./pages/LoyaltyPage'));
const TrashPage          = lazy(() => import('./pages/TrashPage'));
const AIAssistantPage    = lazy(() => import('./pages/AIAssistantPage').then(m => ({ default: m.AIAssistantPage ?? m.default })));
const POSPage            = lazy(() => import('./pages/POSPage').then(m => ({ default: m.POSPage ?? m.default })));
const POSSessionsPage     = lazy(() => import('./pages/POSSessionsPage'));
const POSReportsPage      = lazy(() => import('./pages/POSReportsPage'));
const POSOperationsControlCenterPage = lazy(() => import('./pages/POSOperationsControlCenterPage'));
const ProjectsPage       = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage ?? m.default })));
const ProjectDetailPage  = lazy(() => import('./pages/ProjectDetailPage'));
const BOMPage            = lazy(() => import('./pages/BOMPage'));
const EcommercePage      = lazy(() => import('./pages/EcommercePage').then(m => ({ default: m.EcommercePage ?? m.default })));

// ── HR Expansion (Phase 1) ────────────────────────────────────────────────
const EmployeeDetailPage  = lazy(() => import('./pages/HR/EmployeeDetailPage'));
const DepartmentsPage     = lazy(() => import('./pages/HR/DepartmentsPage'));
const OvertimePage        = lazy(() => import('./pages/HR/OvertimePage'));
const LeaveRequestsPage   = lazy(() => import('./pages/HR/LeaveRequestsPage'));
const LeaveCalendarPage   = lazy(() => import('./pages/HR/LeaveCalendarPage'));
const AttendanceSummaryPage = lazy(() => import('./pages/HR/AttendanceSummaryPage'));
const TimesheetsPage      = lazy(() => import('./pages/HR/TimesheetsPage'));
const PerformancePage     = lazy(() => import('./pages/HR/PerformancePage'));

// ── Payroll & WPS (Phase 2) ───────────────────────────────────────────────
const PayrollRunsPage        = lazy(() => import('./pages/Payroll/PayrollRunsPage'));
const PayRunDetailPage       = lazy(() => import('./pages/Payroll/PayRunDetailPage'));
const SalaryComponentsPage   = lazy(() => import('./pages/Payroll/SalaryComponentsPage'));
const WPSFilePage            = lazy(() => import('./pages/Payroll/WPSFilePage'));
const LoansPage              = lazy(() => import('./pages/Payroll/LoansPage'));
const GratuityCalculatorPage = lazy(() => import('./pages/Payroll/GratuityCalculatorPage'));
const MyPayslipsPage         = lazy(() => import('./pages/Portal/MyPayslipsPage'));

// ── Workflow Engine (Phase 3) ─────────────────────────────────────────────
const WorkflowsListPage    = lazy(() => import('./pages/Automation/WorkflowsListPage'));
const WorkflowBuilderPage  = lazy(() => import('./pages/Automation/WorkflowBuilderPage'));
const WorkflowLogsPage     = lazy(() => import('./pages/Automation/WorkflowLogsPage'));

// ── Approval System (Phase 4) ────────────────────────────────────────────
const ApprovalsInboxPage       = lazy(() => import('./pages/Approvals/ApprovalsInboxPage'));
const ApprovalChainsConfigPage = lazy(() => import('./pages/Approvals/ApprovalChainsConfigPage'));
const ApprovalsMyRequestsPage  = lazy(() => import('./pages/Approvals/MyRequestsPage'));
const ApprovalDetailPage       = lazy(() => import('./pages/Approvals/ApprovalDetailPage'));

// ── Employee Portal (Phase 4) ────────────────────────────────────────────
const EmployeePortalLayout  = lazy(() => import('./pages/Portal/EmployeePortalLayout'));
const EmployeeDashboardPage = lazy(() => import('./pages/Portal/EmployeeDashboardPage'));
const MyProfilePage         = lazy(() => import('./pages/Portal/MyProfilePage'));
const MyLeavesPage          = lazy(() => import('./pages/Portal/MyLeavesPage'));
const MyAttendancePage      = lazy(() => import('./pages/Portal/MyAttendancePage'));
const MyDocumentsPage       = lazy(() => import('./pages/Portal/MyDocumentsPage'));
const PortalMyRequestsPage  = lazy(() => import('./pages/Portal/MyRequestsPage'));

// ── Customer Portal (Phase 4) ────────────────────────────────────────────
const CustomerPortalLayout   = lazy(() => import('./pages/CustomerPortal/CustomerPortalLayout'));
const CustomerLoginPage      = lazy(() => import('./pages/CustomerPortal/LoginPage'));
const CustomerDashboardPage  = lazy(() => import('./pages/CustomerPortal/DashboardPage'));
const CustomerInvoicesPage   = lazy(() => import('./pages/CustomerPortal/InvoicesPage'));
const CustomerQuotationsPage = lazy(() => import('./pages/CustomerPortal/QuotationsPage'));
const CustomerStatementsPage = lazy(() => import('./pages/CustomerPortal/StatementsPage'));
const CustomerPaymentsPage   = lazy(() => import('./pages/CustomerPortal/PaymentsPage'));
const CustomerTicketsPage    = lazy(() => import('./pages/CustomerPortal/TicketsPage'));

// ── Vendor Portal (Phase 4) ──────────────────────────────────────────────
const VendorPortalLayout       = lazy(() => import('./pages/VendorPortal/VendorPortalLayout'));
const VendorLoginPage          = lazy(() => import('./pages/VendorPortal/LoginPage'));
const VendorDashboardPage      = lazy(() => import('./pages/VendorPortal/DashboardPage'));
const VendorPurchaseOrdersPage = lazy(() => import('./pages/VendorPortal/PurchaseOrdersPage'));
const VendorInvoicesPage       = lazy(() => import('./pages/VendorPortal/InvoicesPage'));
const VendorStatementsPage     = lazy(() => import('./pages/VendorPortal/StatementsPage'));
const VendorPaymentsPage       = lazy(() => import('./pages/VendorPortal/PaymentsPage'));

// Admin / Settings
const UsersPage          = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage ?? m.default })));
const CompanySettingsPage = lazy(() => import('./pages/CompanySettingsPage').then(m => ({ default: m.CompanySettingsPage ?? m.default })));
const ProfilePage        = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage ?? m.default })));
const ZATCASettingsPage  = lazy(() => import('./pages/ZATCASettingsPage').then(m => ({ default: m.ZATCASettingsPage ?? m.default })));

// ── Phase 5: Custom Fields, Reports, AI, Notifications, Audit ────────────
const CustomFieldsPage           = lazy(() => import('./pages/Settings/CustomFieldsPage'));
const AuditLogPage               = lazy(() => import('./pages/Settings/AuditLogPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/Settings/NotificationPreferencesPage'));
const MFASettingsPage             = lazy(() => import('./pages/Settings/MFASettingsPage'));
const VATSettingsPage             = lazy(() => import('./pages/Settings/VATSettingsPage'));
const VATReturnPage              = lazy(() => import('./pages/VATReturnPage'));
const ReportBuilderPage          = lazy(() => import('./pages/Reports/ReportBuilderPage'));
const NotificationsPage          = lazy(() => import('./pages/Notifications/NotificationsPage'));

// Advanced / AI modules
const DigitalTwinPage    = lazy(() => import('./pages/DigitalTwinPage'));
const FraudWatchdogPage  = lazy(() => import('./pages/FraudWatchdogPage'));
const AdminObservabilityPage = lazy(() => import('./pages/AdminObservabilityPage'));
const NeuralCloudSpecPage = lazy(() => import('./pages/NeuralCloudSpecPage'));
const CrossModuleOperationsReadinessIndexPage = lazy(() => import('./pages/CrossModuleOperationsReadinessIndexPage'));
const HRIntelligencePage = lazy(() => import('./pages/HRIntelligencePage'));
const ZeroUIPage         = lazy(() => import('./pages/ZeroUIPage'));
const IntelligentGovernancePlatformPage = lazy(() => import('./pages/IntelligentGovernancePlatformPage'));

// ── International Trade & Supply Chain (Phase 3) ──────────────────────────────
const CustomsClearancePage = lazy(() => import('./pages/CustomsClearancePage'));
const ShipmentsPage_IT     = lazy(() => import('./pages/ShipmentsPage'));
const FreeZonesPage        = lazy(() => import('./pages/FreeZonesPage'));
const SupplyChainPage_IT   = lazy(() => import('./pages/SupplyChainPage'));
const TradeDashboardPage   = lazy(() => import('./pages/TradeDashboardPage'));
// Phase 3
const CompliancePage       = lazy(() => import('./pages/CompliancePage'));
const ReExportPage         = lazy(() => import('./pages/ReExportPage'));
const RiskAssessmentPage   = lazy(() => import('./pages/RiskAssessmentPage'));
const CostOptimizationPage = lazy(() => import('./pages/CostOptimizationPage'));

// Error pages
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'));

// ─── Route guards ─────────────────────────────────────────────────────────
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return !user ? children : <Navigate to="/dashboard" replace />;
};

// ─── Portal route guard ───────────────────────────────────────────────────
// Customer/Vendor portals use a separate token-based session (set by their
// own OTP login), distinct from the staff useAuth() session. The route
// itself is guarded here — not only the in-page token check inside each
// portal layout — so portal pages never mount without a token. Token
// signature/expiry are validated server-side on every API call; the frontend
// only gates on token presence, the same trust model as PrivateRoute (which
// gates on presence of the staff user object).
const PortalRoute = ({ tokenKey, loginPath, children }) => {
  let token = null;
  try {
    token = localStorage.getItem(tokenKey);
  } catch {
    token = null;
  }
  return token ? children : <Navigate to={loginPath} replace />;
};

// ─── AI Chat Widget (only rendered when authenticated) ────────────────────
const AuthenticatedAIChatWidget = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <AIChatWidget />;
};

// ─── App ──────────────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <CurrencyProvider>
          <BranchProvider>
          <SuccessCelebrationProvider>
          <DensityProvider>
          <BrowserRouter>
            {/* Keeps the browser tab title in sync with the active route */}
            <DocumentTitle />
            {/* Accessibility: skip-to-content link for keyboard users */}
            <a href="#main-content" className="skip-to-content">
              تخطي إلى المحتوى الرئيسي
            </a>

            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* ── Public Routes ─────────────────────────────── */}
                <Route path="/login"      element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/register"   element={<PublicRoute><RegisterPage /></PublicRoute>} />
                <Route path="/storefront" element={<StorefrontPage />} />

                {/* ── Dashboards ────────────────────────────────── */}
                <Route path="/dashboard"
                  element={<PrivateRoute><Layout><PageErrorBoundary><UnifiedDashboard /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/dashboard-classic" element={<Navigate to="/dashboard" replace />} />

                {/* ── Core ERP ──────────────────────────────────── */}
                <Route path="/customers"
                  element={<PrivateRoute><Layout><CustomersPage /></Layout></PrivateRoute>} />
                <Route path="/customers/:customerId/360"
                  element={<PrivateRoute><Layout><PageErrorBoundary><Customer360Page /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/suppliers"
                  element={<PrivateRoute><Layout><SuppliersPage /></Layout></PrivateRoute>} />
                <Route path="/inventory"
                  element={<PrivateRoute><Layout><InventoryOverviewPage /></Layout></PrivateRoute>} />
                <Route path="/products"
                  element={<PrivateRoute><Layout><ProductsPage /></Layout></PrivateRoute>} />
                <Route path="/employees"
                  element={<PrivateRoute><Layout><EmployeesPage /></Layout></PrivateRoute>} />
                <Route path="/employees/:id"
                  element={<PrivateRoute><Layout><EmployeeDetailPage /></Layout></PrivateRoute>} />
                <Route path="/hr/departments"
                  element={<PrivateRoute><Layout><PageErrorBoundary><DepartmentsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr/overtime"
                  element={<PrivateRoute><Layout><PageErrorBoundary><OvertimePage /></PageErrorBoundary></Layout></PrivateRoute>} />
                {/* ── Route Aliases (sidebar links → canonical paths) ─────── */}
                <Route path="/hr/employees"           element={<Navigate to="/employees" replace />} />
                <Route path="/automation/workflows"   element={<Navigate to="/automation" replace />} />
                <Route path="/settings/users"         element={<Navigate to="/users" replace />} />
                <Route path="/settings/company"       element={<Navigate to="/settings" replace />} />
                {/* ── HR Expansion ───────────────────────────── */}
                <Route path="/hr/leaves"
                  element={<PrivateRoute><Layout><PageErrorBoundary><LeaveRequestsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr/leave-calendar"
                  element={<PrivateRoute><Layout><PageErrorBoundary><LeaveCalendarPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr/attendance"
                  element={<PrivateRoute><Layout><PageErrorBoundary><AttendanceSummaryPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr/timesheets"
                  element={<PrivateRoute><Layout><PageErrorBoundary><TimesheetsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr/performance"
                  element={<PrivateRoute><Layout><PageErrorBoundary><PerformancePage /></PageErrorBoundary></Layout></PrivateRoute>} />
                {/* ── Payroll & WPS (Phase 2) ─────────────────── */}
                <Route path="/payroll/runs"
                  element={<PrivateRoute><Layout><PageErrorBoundary><PayrollRunsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payroll/runs/:id"
                  element={<PrivateRoute><Layout><PageErrorBoundary><PayRunDetailPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payroll/components"
                  element={<PrivateRoute><Layout><PageErrorBoundary><SalaryComponentsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payroll/wps"
                  element={<PrivateRoute><Layout><PageErrorBoundary><WPSFilePage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payroll/loans"
                  element={<PrivateRoute><Layout><PageErrorBoundary><LoansPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payroll/gratuity"
                  element={<PrivateRoute><Layout><PageErrorBoundary><GratuityCalculatorPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                {/* ── Approval System (Phase 4) ───────────────── */}
                <Route path="/approvals/inbox"
                  element={<PrivateRoute><Layout><PageErrorBoundary><ApprovalsInboxPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/approvals/chains"
                  element={<PrivateRoute><Layout><PageErrorBoundary><ApprovalChainsConfigPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/approvals/my-requests"
                  element={<PrivateRoute><Layout><PageErrorBoundary><ApprovalsMyRequestsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/approvals/requests/:id"
                  element={<PrivateRoute><Layout><PageErrorBoundary><ApprovalDetailPage /></PageErrorBoundary></Layout></PrivateRoute>} />

                {/* ── Employee Portal (Phase 4) ─────────────────── */}
                <Route path="/portal/my-payslips"
                  element={<PrivateRoute><Layout><PageErrorBoundary><MyPayslipsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/portal" element={<PrivateRoute><EmployeePortalLayout /></PrivateRoute>}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard"    element={<PageErrorBoundary><EmployeeDashboardPage /></PageErrorBoundary>} />
                  <Route path="my-profile"   element={<PageErrorBoundary><MyProfilePage /></PageErrorBoundary>} />
                  <Route path="my-leaves"    element={<PageErrorBoundary><MyLeavesPage /></PageErrorBoundary>} />
                  <Route path="my-attendance" element={<PageErrorBoundary><MyAttendancePage /></PageErrorBoundary>} />
                  <Route path="my-documents" element={<PageErrorBoundary><MyDocumentsPage /></PageErrorBoundary>} />
                  <Route path="my-requests"  element={<PageErrorBoundary><PortalMyRequestsPage /></PageErrorBoundary>} />
                </Route>
                {/* ── Workflow Engine (Phase 3) ────────────────── */}
                <Route path="/automation"
                  element={<PrivateRoute><Layout><PageErrorBoundary><WorkflowsListPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/automation/builder"
                  element={<PrivateRoute><Layout><PageErrorBoundary><WorkflowBuilderPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/automation/builder/:id"
                  element={<PrivateRoute><Layout><PageErrorBoundary><WorkflowBuilderPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/automation/logs/:id"
                  element={<PrivateRoute><Layout><PageErrorBoundary><WorkflowLogsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/payments"
                  element={<PrivateRoute><Layout><PaymentsOverviewPage /></Layout></PrivateRoute>} />
                <Route path="/invoices"
                  element={<PrivateRoute><Layout><InvoicesPage /></Layout></PrivateRoute>} />
                <Route path="/recurring-invoices"
                  element={<PrivateRoute><Layout><PageErrorBoundary><RecurringInvoicesPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/invoices/create"
                  element={<PrivateRoute><Layout><CreateInvoicePage /></Layout></PrivateRoute>} />
                <Route path="/invoices/:id"
                  element={<PrivateRoute><Layout><InvoiceDetailsPage /></Layout></PrivateRoute>} />
                <Route path="/sales"
                  element={<PrivateRoute><Layout><SalesPage /></Layout></PrivateRoute>} />
                <Route path="/purchases"
                  element={<PrivateRoute><Layout><PurchasesPage /></Layout></PrivateRoute>} />
                <Route path="/expenses"
                  element={<PrivateRoute><Layout><ExpensesPage /></Layout></PrivateRoute>} />
                <Route path="/reports"
                  element={<PrivateRoute><Layout><ReportsPage /></Layout></PrivateRoute>} />
                <Route path="/quotations"
                  element={<PrivateRoute><Layout><QuotationsPage /></Layout></PrivateRoute>} />
                <Route path="/purchase-orders"
                  element={<PrivateRoute><Layout><PurchaseOrdersPage /></Layout></PrivateRoute>} />
                <Route path="/crm"
                  element={<PrivateRoute><Layout><CRMPage /></Layout></PrivateRoute>} />
                <Route path="/manufacturing"
                  element={<PrivateRoute><Layout><ManufacturingPage /></Layout></PrivateRoute>} />
                <Route path="/manufacturing/roadmap"
                  element={<PrivateRoute><Layout><ManufacturingRoadmapPage /></Layout></PrivateRoute>} />
                <Route path="/warehouses"
                  element={<PrivateRoute><Layout><WarehousesPage /></Layout></PrivateRoute>} />
                <Route path="/branches"
                  element={<PrivateRoute><Layout><BranchesPage /></Layout></PrivateRoute>} />
                <Route path="/promotions"
                  element={<PrivateRoute><Layout><PromotionsPage /></Layout></PrivateRoute>} />
                <Route path="/loyalty"
                  element={<PrivateRoute><Layout><LoyaltyPage /></Layout></PrivateRoute>} />
                <Route path="/stock-moves"
                  element={<PrivateRoute><Layout><StockMovesPage /></Layout></PrivateRoute>} />
                {/* ── International Trade & Supply Chain ── */}
                <Route path="/trade-dashboard"
                  element={<PrivateRoute><Layout><TradeDashboardPage /></Layout></PrivateRoute>} />
                <Route path="/customs"
                  element={<PrivateRoute><Layout><CustomsClearancePage /></Layout></PrivateRoute>} />
                <Route path="/shipments"
                  element={<PrivateRoute><Layout><ShipmentsPage_IT /></Layout></PrivateRoute>} />
                <Route path="/free-zones"
                  element={<PrivateRoute><Layout><FreeZonesPage /></Layout></PrivateRoute>} />
                <Route path="/supply-chain"
                  element={<PrivateRoute><Layout><SupplyChainPage_IT /></Layout></PrivateRoute>} />
                <Route path="/compliance"
                  element={<PrivateRoute><Layout><CompliancePage /></Layout></PrivateRoute>} />
                <Route path="/re-export"
                  element={<PrivateRoute><Layout><ReExportPage /></Layout></PrivateRoute>} />
                <Route path="/risk-assessment"
                  element={<PrivateRoute><Layout><RiskAssessmentPage /></Layout></PrivateRoute>} />
                <Route path="/cost-optimization"
                  element={<PrivateRoute><Layout><CostOptimizationPage /></Layout></PrivateRoute>} />
                <Route path="/journal-entries"
                  element={<PrivateRoute><Layout><JournalEntriesPage /></Layout></PrivateRoute>} />
                <Route path="/financial-reports"
                  element={<PrivateRoute><Layout><FinancialReportsPage /></Layout></PrivateRoute>} />
                <Route path="/banks/reconciliation"
                  element={<PrivateRoute><Layout><BankReconciliationPage /></Layout></PrivateRoute>} />
                <Route path="/banks"
                  element={<PrivateRoute><Layout><BanksPage /></Layout></PrivateRoute>} />
                <Route path="/trash"
                  element={<PrivateRoute><Layout><TrashPage /></Layout></PrivateRoute>} />
                <Route path="/accounting"
                  element={<PrivateRoute><Layout><AccountingPage /></Layout></PrivateRoute>} />
                <Route path="/accounting/close-dashboard"
                  element={<PrivateRoute><Layout><AccountingCloseDashboardPage /></Layout></PrivateRoute>} />
                <Route path="/ai-assistant"
                  element={<PrivateRoute><Layout><AIAssistantPage /></Layout></PrivateRoute>} />
                <Route path="/projects"
                  element={<PrivateRoute><Layout><ProjectsPage /></Layout></PrivateRoute>} />
                <Route path="/projects/:id"
                  element={<PrivateRoute><Layout><ProjectDetailPage /></Layout></PrivateRoute>} />
                <Route path="/manufacturing/bom"
                  element={<PrivateRoute><Layout><BOMPage /></Layout></PrivateRoute>} />
                <Route path="/ecommerce"
                  element={<PrivateRoute><Layout><EcommercePage /></Layout></PrivateRoute>} />

                {/* ── Customer Portal (Phase 4) ────────────────── */}
                <Route path="/customer-portal/login" element={<CustomerLoginPage />} />
                <Route path="/customer-portal" element={<PortalRoute tokenKey="customer_portal_token" loginPath="/customer-portal/login"><CustomerPortalLayout /></PortalRoute>}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard"   element={<CustomerDashboardPage />} />
                  <Route path="invoices"    element={<CustomerInvoicesPage />} />
                  <Route path="quotations"  element={<CustomerQuotationsPage />} />
                  <Route path="statements"  element={<CustomerStatementsPage />} />
                  <Route path="payments"    element={<CustomerPaymentsPage />} />
                  <Route path="tickets"     element={<CustomerTicketsPage />} />
                </Route>

                {/* ── Vendor Portal (Phase 4) ──────────────────── */}
                <Route path="/vendor-portal/login" element={<VendorLoginPage />} />
                <Route path="/vendor-portal" element={<PortalRoute tokenKey="vendor_portal_token" loginPath="/vendor-portal/login"><VendorPortalLayout /></PortalRoute>}>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard"       element={<VendorDashboardPage />} />
                  <Route path="purchase-orders" element={<VendorPurchaseOrdersPage />} />
                  <Route path="invoices"        element={<VendorInvoicesPage />} />
                  <Route path="statements"      element={<VendorStatementsPage />} />
                  <Route path="payments"        element={<VendorPaymentsPage />} />
                </Route>

                {/* POS — full screen, no Layout sidebar */}
                <Route path="/pos"
                  element={<PrivateRoute><POSPage /></PrivateRoute>} />
                {/* Sprint-4 — POS sessions management (uses Layout sidebar) */}
                <Route path="/pos/sessions"
                  element={<PrivateRoute><Layout><POSSessionsPage /></Layout></PrivateRoute>} />
                {/* POS-6 — advanced reports admin */}
                <Route path="/pos/reports"
                  element={<PrivateRoute><Layout><POSReportsPage /></Layout></PrivateRoute>} />
                {/* POS Operations Control Center — read-only UX/spec */}
                <Route path="/pos/operations-control-center"
                  element={<PrivateRoute><Layout><POSOperationsControlCenterPage /></Layout></PrivateRoute>} />

                {/* ── Phase 5: Custom Fields / Reports / Notifications / Audit ── */}
                <Route path="/settings/custom-fields"
                  element={<PrivateRoute><Layout><PageErrorBoundary><CustomFieldsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/settings/audit-log"
                  element={<PrivateRoute><Layout><PageErrorBoundary><AuditLogPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/settings/notification-preferences"
                  element={<PrivateRoute><Layout><PageErrorBoundary><NotificationPreferencesPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/settings/mfa"
                  element={<PrivateRoute><Layout><PageErrorBoundary><MFASettingsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/settings/vat"
                  element={<PrivateRoute><Layout><PageErrorBoundary><VATSettingsPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/vat-return"
                  element={<PrivateRoute><Layout><PageErrorBoundary><VATReturnPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/reports/builder"
                  element={<PrivateRoute><Layout><PageErrorBoundary><ReportBuilderPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/notifications"
                  element={<PrivateRoute><Layout><PageErrorBoundary><NotificationsPage /></PageErrorBoundary></Layout></PrivateRoute>} />

                {/* ── Admin / Settings ──────────────────────────── */}
                <Route path="/users"
                  element={<PrivateRoute><Layout><UsersPage /></Layout></PrivateRoute>} />
                <Route path="/settings"
                  element={<PrivateRoute><Layout><CompanySettingsPage /></Layout></PrivateRoute>} />
                <Route path="/zatca"
                  element={<PrivateRoute><Layout><ZATCASettingsPage /></Layout></PrivateRoute>} />
                <Route path="/profile"
                  element={<PrivateRoute><Layout><ProfilePage /></Layout></PrivateRoute>} />

                {/* ── Advanced / AI Modules ─────────────────────── */}
                <Route path="/intelligent-governance"
                  element={<PrivateRoute><Layout><PageErrorBoundary><IntelligentGovernancePlatformPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/digital-twin"
                  element={<PrivateRoute><Layout><DigitalTwinPage /></Layout></PrivateRoute>} />
                <Route path="/fraud-watchdog"
                  element={<PrivateRoute><Layout><FraudWatchdogPage /></Layout></PrivateRoute>} />
                <Route path="/admin/observability"
                  element={<PrivateRoute><Layout><AdminObservabilityPage /></Layout></PrivateRoute>} />
                <Route path="/neural-cloud"
                  element={<PrivateRoute><Layout><PageErrorBoundary><NeuralCloudSpecPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/operations-readiness-index"
                  element={<PrivateRoute><Layout><PageErrorBoundary><CrossModuleOperationsReadinessIndexPage /></PageErrorBoundary></Layout></PrivateRoute>} />
                <Route path="/hr-intelligence"
                  element={<PrivateRoute><Layout><HRIntelligencePage /></Layout></PrivateRoute>} />
                {/* ZeroUI — full screen, no Layout sidebar */}
                <Route path="/zero-ui"
                  element={<PrivateRoute><ZeroUIPage /></PrivateRoute>} />

                {/* ── Legacy aliases ────────────────────────────── */}
                <Route path="/adaptive-dashboard" element={<Navigate to="/dashboard" replace />} />
                <Route path="/neural-command"     element={<Navigate to="/dashboard" replace />} />
                <Route path="/create-invoice"     element={<Navigate to="/invoices/create" replace />} />
                <Route path="/vat/settings"       element={<Navigate to="/settings/vat" replace />} />
                <Route path="/vat/return"         element={<Navigate to="/vat-return" replace />} />
                <Route path="/bank-reconciliation" element={<Navigate to="/banks/reconciliation" replace />} />

                {/* ── Default / 404 ─────────────────────────────── */}
                <Route path="/"   element={<Navigate to="/dashboard" replace />} />
                <Route path="*"   element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            {/* AI Chat Widget — only visible when authenticated */}
            <AuthenticatedAIChatWidget />

            {/* Zia-style Floating AI Assistant — context-aware per page */}
            <FloatingAIAssistant />
          </BrowserRouter>
          </DensityProvider>
          </SuccessCelebrationProvider>
          </BranchProvider>
          </CurrencyProvider>

          {/* Global toast notifications */}
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
